import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Badge, LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  LocalHospital as DoctorIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  MedicalServices as MedicalIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { api } from '@services/authService';
import { StatusChip } from '@shared/StatusChip';
import EmptyState from '@shared/EmptyState';
import { formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';
import type {
  AppointmentsReportData,
  FinanceReportData,
  InventoryReportData,
  ServicesReportData,
  DoctorsReportData,
} from '@app-types/clinicReports';

interface Report {
  id: string;
  name: string;
  type: 'appointments' | 'finance' | 'inventory' | 'services' | 'doctors' | 'custom';
  description: string;
  lastGenerated?: string;
  status: 'available' | 'generating' | 'error';
  size?: string;
  format: 'pdf' | 'excel' | 'csv';
  filters: {
    from?: string;
    to?: string;
    doctorId?: string;
    locationId?: string;
    status?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ReportData {
  appointments: {
    total: number;
    byStatus: { [key: string]: number };
    byDoctor: { [key: string]: number };
    byLocation: { [key: string]: number };
    noShowRate: number;
    averageDuration: number;
  };
  finance: {
    totalRevenue: number;
    byService: { [key: string]: number };
    byDoctor: { [key: string]: number };
    byLocation: { [key: string]: number };
    averageInvoice: number;
    paymentMethods: { [key: string]: number };
  };
  inventory: {
    totalValue: number;
    lowStockItems: number;
    expiredItems: number;
    byCategory: { [key: string]: number };
    turnoverRate: number;
  };
  services: {
    topServices: Array<{ name: string; count: number; revenue: number }>;
    byCategory: { [key: string]: number };
    averagePrice: number;
  };
  doctors: {
    byPerformance: Array<{ name: string; appointments: number; revenue: number; rating: number }>;
    bySpecialty: { [key: string]: number };
    averageRating: number;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/** Filtreaza dupa createdAt (fallback updatedAt). `custom` fara date suplimentare = include tot (pana la UI pentru interval). */
function reportMatchesSelectedPeriod(
  report: Report,
  selectedPeriod: string
): boolean {
  if (selectedPeriod === 'all' || selectedPeriod === 'custom') {
    return true;
  }
  const raw = report.createdAt || report.updatedAt;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) {
    return false;
  }
  const reportDate = new Date(t);
  const now = new Date();

  switch (selectedPeriod) {
    case 'today': {
      return reportDate.toDateString() === now.toDateString();
    }
    case 'week': {
      const start = new Date(now);
      const dow = start.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      start.setDate(now.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return reportDate >= start;
    }
    case 'month':
      return (
        reportDate.getFullYear() === now.getFullYear() &&
        reportDate.getMonth() === now.getMonth()
      );
    case 'quarter': {
      const cq = Math.floor(now.getMonth() / 3);
      const rq = Math.floor(reportDate.getMonth() / 3);
      return reportDate.getFullYear() === now.getFullYear() && cq === rq;
    }
    case 'year':
      return reportDate.getFullYear() === now.getFullYear();
    default:
      return true;
  }
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [rawAppt, setRawAppt] = useState<AppointmentsReportData | null>(null);
  const [rawFin, setRawFin] = useState<FinanceReportData | null>(null);
  const [rawInv, setRawInv] = useState<InventoryReportData | null>(null);
  const [rawSrv, setRawSrv] = useState<ServicesReportData | null>(null);
  const [rawDoc, setRawDoc] = useState<DoctorsReportData | null>(null);

  const buildReportDataFromApi = (
    a: AppointmentsReportData,
    f: FinanceReportData,
    i: InventoryReportData,
    s: ServicesReportData,
    d: DoctorsReportData
  ): ReportData => {
    const byDoctorAppt: Record<string, number> = {};
    a.byVeterinarian.forEach((row) => {
      const k = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
      if (k) byDoctorAppt[k] = row.count;
    });
    const lineEntries = Object.entries(s.byInvoiceLine);
    const topServices = lineEntries
      .map(([name, v]) => ({ name, count: v.lines, revenue: v.revenueCents / 100 }))
      .sort((x, y) => y.revenue - x.revenue);
    const totalLineRev = lineEntries.reduce((sum, [, v]) => sum + v.revenueCents, 0);
    const totalLines = lineEntries.reduce((sum, [, v]) => sum + v.lines, 0);
    const byCat: Record<string, number> = {};
    Object.entries(i.byCategory).forEach(([k, v]) => {
      byCat[k] = v.valueCents / 100;
    });
    const ratingVals = d.byDoctor.map((x) => x.averageVisitRating).filter((x): x is number => x != null && !Number.isNaN(x));
    const averageRating = ratingVals.length
      ? Math.round((ratingVals.reduce((a, b) => a + b, 0) / ratingVals.length) * 10) / 10
      : 0;
    return {
      appointments: {
        total: a.total,
        byStatus: a.byStatus,
        byDoctor: byDoctorAppt,
        byLocation: {},
        noShowRate: a.noShowRate,
        averageDuration: a.averageDurationMinutes,
      },
      finance: {
        totalRevenue: f.totalRevenueCents / 100,
        byService: Object.fromEntries(
          lineEntries.map(([k, v]) => [k, v.revenueCents / 100])
        ),
        byDoctor: Object.fromEntries(
          f.byVeterinarian.map((x) => [
            [x.firstName, x.lastName].join(' ').trim(),
            x.revenueCents / 100,
          ])
        ),
        byLocation: {},
        averageInvoice: f.averageInvoiceCents / 100,
        paymentMethods: Object.fromEntries(
          Object.entries(f.byPaymentMethod).map(([k, v]) => [k, v / 100])
        ),
      },
      inventory: {
        totalValue: i.totalValueCents / 100,
        lowStockItems: i.lowStockCount,
        expiredItems: i.currentlyExpiredBatches,
        byCategory: byCat,
        turnoverRate: i.turnoverRate,
      },
      services: {
        topServices,
        byCategory: Object.fromEntries(
          Object.entries(s.byVisitType).map(([k, v]) => [k, v.revenueCents / 100])
        ),
        averagePrice: totalLines > 0 ? Math.round((totalLineRev / 100 / totalLines) * 100) / 100 : 0,
      },
      doctors: {
        byPerformance: d.byDoctor.map((x) => ({
          name: [x.firstName, x.lastName].join(' ').trim(),
          appointments: x.appointmentCount,
          revenue: x.revenueCents / 100,
          rating: x.averageVisitRating ?? 0,
        })),
        bySpecialty: {},
        averageRating,
      },
    };
  };

  const loadDashboard = useCallback(async () => {
    if (!user?.clinicId) {
      setLoadError('Nu avem clinică asociată contului (clinicId lipsă).');
      setLoading(false);
      setReports([]);
      setRawAppt(null);
      setRawFin(null);
      setRawInv(null);
      setRawSrv(null);
      setRawDoc(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setReports([]);
    setRawAppt(null);
    setRawFin(null);
    setRawInv(null);
    setRawSrv(null);
    setRawDoc(null);
    try {
      const periodMap: Record<string, string> = {
        all: 'all',
        today: 'today',
        week: 'week',
        month: 'month',
        quarter: 'quarter',
        year: 'year',
        custom: 'all',
      };
      const period = periodMap[selectedPeriod] || 'all';
      const params = { period, clinicId: user.clinicId };
      const [ap, fi, inv, sv, doc] = await Promise.all([
        api.get<{ data: AppointmentsReportData }>('/clinic/reports/appointments', { params }),
        api.get<{ data: FinanceReportData }>('/clinic/reports/finance', { params }),
        api.get<{ data: InventoryReportData }>('/clinic/reports/inventory', { params }),
        api.get<{ data: ServicesReportData }>('/clinic/reports/services', { params }),
        api.get<{ data: DoctorsReportData }>('/clinic/reports/doctors', { params }),
      ]);
      const a = ap.data.data;
      const f = fi.data.data;
      const i = inv.data.data;
      const s = sv.data.data;
      const d = doc.data.data;
      setRawAppt(a);
      setRawFin(f);
      setRawInv(i);
      setRawSrv(s);
      setRawDoc(d);
      setReportData(buildReportDataFromApi(a, f, i, s, d));
      const ts = new Date().toISOString();
      setReports([
        {
          id: 'rep-appointments',
          name: 'Programari',
          type: 'appointments',
          description: `Total: ${a.total}, perioada: ${a.period}.`,
          lastGenerated: ts,
          status: 'available',
          size: '—',
          format: 'csv',
          filters: { from: a.period },
          createdAt: ts,
          updatedAt: ts,
        },
        {
          id: 'rep-finance',
          name: 'Financiar',
          type: 'finance',
          description: `Facturi: ${f.invoiceCount}, venit: ${(f.totalRevenueCents / 100).toFixed(2)} RON, perioada: ${f.period}.`,
          lastGenerated: ts,
          status: 'available',
          size: '—',
          format: 'csv',
          filters: { from: f.period },
          createdAt: ts,
          updatedAt: ts,
        },
        {
          id: 'rep-inventory',
          name: 'Inventar',
          type: 'inventory',
          description: `Valoare stoc: ${(i.totalValueCents / 100).toFixed(2)} RON, sub prag: ${i.lowStockCount}, tranzactii: ${i.transactionCountInPeriod}.`,
          lastGenerated: ts,
          status: 'available',
          size: '—',
          format: 'csv',
          filters: { from: i.period },
          createdAt: ts,
          updatedAt: ts,
        },
        {
          id: 'rep-services',
          name: 'Servicii',
          type: 'services',
          description: `Tipuri vizită: ${Object.keys(s.byVisitType).length}, linii factura: ${Object.keys(s.byInvoiceLine).length}, perioada: ${s.period}.`,
          lastGenerated: ts,
          status: 'available',
          size: '—',
          format: 'csv',
          filters: { from: s.period },
          createdAt: ts,
          updatedAt: ts,
        },
        {
          id: 'rep-doctors',
          name: 'Doctori',
          type: 'doctors',
          description: `Echipa: ${d.byDoctor.length} veterinari, perioada: ${d.period}.`,
          lastGenerated: ts,
          status: 'available',
          size: '—',
          format: 'csv',
          filters: { from: d.period },
          createdAt: ts,
          updatedAt: ts,
        },
      ]);
    } catch (e: unknown) {
      console.error('Eroare la incarcarea rapoartelor clinic:', e);
      setLoadError('Nu am putut incarca rapoartele de la server.');
      setReportData({
        appointments: { total: 0, byStatus: {}, byDoctor: {}, byLocation: {}, noShowRate: 0, averageDuration: 0 },
        finance: { totalRevenue: 0, byService: {}, byDoctor: {}, byLocation: {}, averageInvoice: 0, paymentMethods: {} },
        inventory: { totalValue: 0, lowStockItems: 0, expiredItems: 0, byCategory: {}, turnoverRate: 0 },
        services: { topServices: [], byCategory: {}, averagePrice: 0 },
        doctors: { byPerformance: [], bySpecialty: {}, averageRating: 0 },
      });
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId, selectedPeriod]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'appointments':
        return 'primary';
      case 'finance':
        return 'success';
      case 'inventory':
        return 'warning';
      case 'services':
        return 'info';
      case 'doctors':
        return 'secondary';
      case 'custom':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'appointments':
        return 'Programari';
      case 'finance':
        return 'Financiar';
      case 'inventory':
        return 'Inventar';
      case 'services':
        return 'Servicii';
      case 'doctors':
        return 'Doctori';
      case 'custom':
        return 'Personalizat';
      default:
        return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'appointments':
        return <CalendarIcon />;
      case 'finance':
        return <MoneyIcon />;
      case 'inventory':
        return <InventoryIcon />;
      case 'services':
        return <MedicalIcon />;
      case 'doctors':
        return <DoctorIcon />;
      case 'custom':
        return <AssessmentIcon />;
      default:
        return <AssessmentIcon />;
    }
  };

  const _getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'generating':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const _getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Disponibil';
      case 'generating':
        return 'Se genereaza';
      case 'error':
        return 'Eroare';
      default:
        return status;
    }
  };

  const mapReportStatus = (status: string) => {
    switch (status) {
      case 'available':
        return 'confirmed'; // Map to success status
      case 'generating':
        return 'requested'; // Map to warning status
      case 'error':
        return 'cancelled'; // Map to error status
      default:
        return 'requested';
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'pdf':
        return <PrintIcon />;
      case 'excel':
        return <BarChartIcon />;
      case 'csv':
        return <ShowChartIcon />;
      default:
        return <AssessmentIcon />;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const scrollToReportStats = () => {
    document.getElementById('reports-stats')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const downloadReportCsv = useCallback(
    (report: Report) => {
      const esc = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`;
      let rows: string[][] = [];
      if (report.type === 'appointments' && rawAppt) {
        rows = [
          ['clinicId', 'period', 'total', 'noShowRate', 'averageDurationMinutes'],
          [
            rawAppt.clinicId,
            rawAppt.period,
            String(rawAppt.total),
            String(rawAppt.noShowRate),
            String(rawAppt.averageDurationMinutes),
          ],
        ];
        rows.push(['status', 'count']);
        Object.entries(rawAppt.byStatus).forEach(([k, v]) => rows.push([k, String(v)]));
        rows.push(['veterinarianId', 'nume', 'programari']);
        rawAppt.byVeterinarian.forEach((r) => {
          rows.push([
            r.veterinarianId ?? '',
            [r.firstName, r.lastName].join(' ').trim(),
            String(r.count),
          ]);
        });
      } else if (report.type === 'finance' && rawFin) {
        rows = [
          ['clinicId', 'period', 'totalRevenueCents', 'invoiceCount', 'averageInvoiceCents'],
          [
            rawFin.clinicId,
            rawFin.period,
            String(rawFin.totalRevenueCents),
            String(rawFin.invoiceCount),
            String(rawFin.averageInvoiceCents),
          ],
        ];
        rows.push(['payment_method', 'amount_cents']);
        Object.entries(rawFin.byPaymentMethod).forEach(([k, v]) => rows.push([k, String(v)]));
        rows.push(['userId', 'nume', 'revenueCents', 'invoices']);
        rawFin.byVeterinarian.forEach((r) => {
          rows.push([
            r.userId,
            [r.firstName, r.lastName].join(' ').trim(),
            String(r.revenueCents),
            String(r.invoiceCount),
          ]);
        });
      } else if (report.type === 'inventory' && rawInv) {
        rows = [
          [
            'clinicId',
            'period',
            'totalValueCents',
            'lowStockCount',
            'batchesWithExpirationInPeriod',
            'currentlyExpiredBatches',
            'transactionCountInPeriod',
            'consumptionUnitsInPeriod',
            'turnoverRate',
          ],
          [
            rawInv.clinicId,
            rawInv.period,
            String(rawInv.totalValueCents),
            String(rawInv.lowStockCount),
            String(rawInv.batchesWithExpirationInPeriod),
            String(rawInv.currentlyExpiredBatches),
            String(rawInv.transactionCountInPeriod),
            String(rawInv.consumptionUnitsInPeriod),
            String(rawInv.turnoverRate),
          ],
        ];
        rows.push(['categorie', 'valoare_cents', 'cantitate']);
        Object.entries(rawInv.byCategory).forEach(([k, v]) => {
          rows.push([k, String(v.valueCents), String(v.quantity)]);
        });
      } else if (report.type === 'services' && rawSrv) {
        rows = [['clinicId', 'period'], [rawSrv.clinicId, rawSrv.period]];
        rows.push(['visit_type', 'visits', 'revenueCents']);
        Object.entries(rawSrv.byVisitType).forEach(([k, v]) => {
          rows.push([k, String(v.visits), String(v.revenueCents)]);
        });
        rows.push(['line_description', 'lines', 'revenueCents']);
        Object.entries(rawSrv.byInvoiceLine).forEach(([k, v]) => {
          rows.push([k, String(v.lines), String(v.revenueCents)]);
        });
        rows.push(['id', 'name', 'code', 'priceCents', 'durationMinutes', 'active']);
        rawSrv.catalog.forEach((c) => {
          rows.push([c.id, c.name, c.code ?? '', String(c.priceCents), String(c.durationMinutes), String(c.active)]);
        });
      } else if (report.type === 'doctors' && rawDoc) {
        rows = [['clinicId', 'period'], [rawDoc.clinicId, rawDoc.period]];
        rows.push(['userId', 'nume', 'programari', 'vizite', 'revenueCents', 'avgRating']);
        rawDoc.byDoctor.forEach((r) => {
          rows.push([
            r.userId,
            [r.firstName, r.lastName].join(' ').trim(),
            String(r.appointmentCount),
            String(r.visitCount),
            String(r.revenueCents),
            r.averageVisitRating == null ? '' : String(r.averageVisitRating),
          ]);
        });
      } else {
        if (!reportData) return;
        if (report.type === 'appointments') {
          const bs = reportData.appointments.byStatus;
          rows = [
            ['Metrica', 'Valoare'],
            ['Total programari', String(reportData.appointments.total)],
            ['Rata neprezentare (%)', String(reportData.appointments.noShowRate)],
            ['Status', 'count'],
            ...Object.entries(bs).map(([k, v]) => [k, String(v)]),
          ];
        } else {
          return;
        }
      }
      const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raport-${report.type}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [reportData, rawAppt, rawFin, rawInv, rawSrv, rawDoc]
  );

  const openReportEmail = (report: Report) => {
    const subject = encodeURIComponent(`HealthyPaws — ${report.name}`);
    const body = encodeURIComponent(`${report.description}\n\n`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || report.type === selectedType;
    const matchesPeriod = reportMatchesSelectedPeriod(report, selectedPeriod);
    const matchesFormat = selectedFormat === 'all' || report.format === selectedFormat;
    
    return matchesSearch && matchesType && matchesPeriod && matchesFormat;
  });

  const appointmentsReports = filteredReports.filter(r => r.type === 'appointments');
  const financeReports = filteredReports.filter(r => r.type === 'finance');
  const inventoryReports = filteredReports.filter(r => r.type === 'inventory');
  const servicesReports = filteredReports.filter(r => r.type === 'services');
  const doctorsReports = filteredReports.filter(r => r.type === 'doctors');
  const allReports = filteredReports;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca rapoartele...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Rapoarte
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analiza operatiuni/finante/inventar - filtre (perioada, doctor, locatie), export CSV/XLSX
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadDashboard()}
          sx={{ borderRadius: 2 }}
        >
          Reîncarcă date
        </Button>
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {/* Statistici */}
      <Grid id="reports-stats" container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  <CalendarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {reportData?.appointments.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Programari Totale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {reportData?.finance.totalRevenue.toLocaleString() || 0} RON
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Venituri Totale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.light' }}>
                  <InventoryIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {reportData?.inventory.totalValue.toLocaleString() || 0} RON
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valoare Inventar
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {reportData?.doctors.averageRating || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rating Mediu Doctori
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta raport..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  label="Tip"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="appointments">Programari</MenuItem>
                  <MenuItem value="finance">Financiar</MenuItem>
                  <MenuItem value="inventory">Inventar</MenuItem>
                  <MenuItem value="services">Servicii</MenuItem>
                  <MenuItem value="doctors">Doctori</MenuItem>
                  <MenuItem value="custom">Personalizat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Perioada</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="Perioada"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="today">Astazi</MenuItem>
                  <MenuItem value="week">Saptamana aceasta</MenuItem>
                  <MenuItem value="month">Luna aceasta</MenuItem>
                  <MenuItem value="quarter">Trimestrul acesta</MenuItem>
                  <MenuItem value="year">Anul acesta</MenuItem>
                  <MenuItem value="custom">Personalizat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  label="Format"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                sx={{ height: 56, borderRadius: 2 }}
                onClick={() => loadDashboard()}
              >
                Aplica filtre
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Reincarca">
                <IconButton onClick={() => loadDashboard()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="reports tabs">
            <Tab 
              label={
                <Badge badgeContent={appointmentsReports.length} color="primary">
                  Programari
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={financeReports.length} color="success">
                  Financiar
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={inventoryReports.length} color="warning">
                  Inventar
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={servicesReports.length} color="info">
                  Servicii
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={doctorsReports.length} color="secondary">
                  Doctori
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={allReports.length} color="default">
                  Toate
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointmentsReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {appointmentsReports.length === 0 && (
            <EmptyState
              icon={<AssessmentIcon />}
              title="Niciun raport de programari"
              description="Nu exista rapoarte generate pentru programari. Genereaza primul raport pentru a incepe."
              action={{
                label: 'Reincarca date',
                onClick: () => loadDashboard()
              }}
            />
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={appointmentsReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financeReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'success.light' }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={financeReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={inventoryReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servicesReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.light' }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={servicesReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctorsReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'secondary.light' }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={doctorsReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Raport</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Ultima Generare</TableCell>
                  <TableCell>Marime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allReports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: report.type === 'appointments' ? 'primary.light' : 
                                    report.type === 'finance' ? 'success.light' : 
                                    report.type === 'inventory' ? 'warning.light' : 
                                    report.type === 'services' ? 'info.light' : 
                                    report.type === 'doctors' ? 'secondary.light' : 'default.light'
                          }}>
                            {getTypeIcon(report.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {report.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(report.type)} 
                          size="small" 
                          color={getTypeColor(report.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFormatIcon(report.format)}
                          <Typography variant="body2">
                            {report.format.toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.lastGenerated ? formatDateOnly(report.lastGenerated) : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.lastGenerated ? formatTimeOnly(report.lastGenerated) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.size || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={mapReportStatus(report.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Tooltip title="Vezi rezumat (sus)">
                            <IconButton size="small" onClick={() => scrollToReportStats()} aria-label="Vezi rezumat">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descarca CSV">
                            <IconButton size="small" onClick={() => downloadReportCsv(report)} aria-label="Descarca CSV">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deschide clientul de email">
                            <IconButton size="small" onClick={() => openReportEmail(report)} aria-label="Trimite pe email">
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allReports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>

    </Box>
  );
};

export default ReportsManagement;

